'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentUser } from '@/store/reducers/appSlice';
import { deleteCookie } from 'cookies-next';
import { User } from '@/components/Types';
import { useState, useRef, useEffect } from 'react';
import { useUserLocale } from '@/hooks/useUserLocale';
import { UserIcon } from '@heroicons/react/24/outline';

export default function UserProfile() {
  const { locale, t } = useUserLocale();
  const router = useRouter();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.app.currentUser);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    // Clear the auth token
    deleteCookie('auth_token');
    
    // Clear the user from Redux state
    dispatch(setCurrentUser(null as unknown as User));
    
    // Redirect to login page
    router.push('/login');
  };

  const handleProfile = () => {
    router.push('/profile');
  };

  const handlePreferences = () => {
    router.push('/preferences');
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, user]);

  return (
    <>
        {user ? (
        <div className="relative" ref={dropdownRef}>
            <button 
            onClick={() => handleProfile()}
            className="focus:outline-none"
            aria-label="User menu"
            >
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center shadow-sm hover:bg-blue-200 transition-colors">
                <span className="text-sm font-bold text-blue-500">
                  <UserIcon className="w-5 h-5" />
                </span>
            </div>
            </button>
            
            {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 py-2 bg-white rounded-lg shadow-xl z-20 translate-x-12">
                <div className="px-4 py-2 border-b border-gray-100">
                <div className="text-sm font-medium text-gray-900">
                    {user.firstName || user.nickname || 'User'}
                </div>
                {user.email && (
                    <div className="text-xs text-gray-500 truncate">
                    {user.email}
                    </div>
                )}
                </div>
                <Link 
                href="/profile" 
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                onClick={() => setIsDropdownOpen(false)}
                >
                Profile
                </Link>
                <Link 
                href="/preferences" 
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                onClick={() => setIsDropdownOpen(false)}
                >
                Preferences
                </Link>
                <hr className="my-1 border-gray-200" />
                <button 
                onClick={() => {
                    setIsDropdownOpen(false);
                    handleLogout();
                }} 
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                >
                Log out
                </button>
            </div>
            )}
        </div>
        ) : (
        <Link 
            href="/login" 
            className="px-4 py-1.5 bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium rounded-full transition-colors shadow-sm"
        >
            Login
        </Link>
        )}
    </>
  );
} 